import React, { useState } from 'react';
import { MessageCircle, Loader2, Plus } from 'lucide-react';
import { useConversationList } from '@/hooks/useConversationList';
import { whatsappInboxService, WhatsAppConversation, ConversationStatus } from '@/services/integrations/whatsappInboxService';
import SearchFilterBar from './SearchFilterBar';
import ConversationListItem from './ConversationListItem';
import StartConversationModal from './StartConversationModal';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';

interface ConversationListProps {
  activeUuid: string | null;
  onSelect: (conversation: WhatsAppConversation) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ activeUuid, onSelect }) => {
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [status, setStatus] = useState<ConversationStatus | undefined>(undefined);
  const [assignedUserUuid, setAssignedUserUuid] = useState<string | undefined>(undefined);

  const { conversations, loading, error, page, totalPages, goToPage, patchConversation, refresh } =
    useConversationList({ search: search.trim() || undefined, unreadOnly, status, assignedUserUuid });

  const [showStartModal, setShowStartModal] = useState(false);

  const unreadTotal = conversations.reduce((sum, c) => sum + (c.unread_count > 0 ? 1 : 0), 0);

  const handleSelect = async (c: WhatsAppConversation) => {
    onSelect(c);
    if (c.unread_count === 0) return;

    // Clear the badge immediately, then tell the server. The optimistic patch used
    // to be the ONLY thing that happened -- whatsappInboxService.markRead existed
    // and POST /{uuid}/read existed, but nothing ever called it. unread_count stayed
    // untouched in the DB, so the badge came back on the next list refresh (every
    // socket event, and every poll while degraded) no matter how many times the
    // conversation was opened.
    patchConversation(c.uuid, { unread_count: 0 });
    const ok = await whatsappInboxService.markRead(c.uuid);
    if (!ok) patchConversation(c.uuid, { unread_count: c.unread_count });
  };

  return (
    <div className="w-[372px] flex-shrink-0 bg-white border-r border-gray-200 flex flex-col min-h-0">
      <div className="px-4 pt-3.5 pb-0">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-[#153d23] flex items-center justify-center flex-shrink-0">
            <WhatsAppIcon className="w-[17px] h-[17px] text-white" />
          </div>
          <div className="flex-1">
            <div className="text-[15.5px] font-bold tracking-tight leading-tight">WhatsApp</div>
          </div>
          <button
            onClick={() => setShowStartModal(true)}
            title="New conversation"
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        unreadOnly={unreadOnly}
        onToggleUnread={() => setUnreadOnly((v) => !v)}
        status={status}
        onStatusChange={setStatus}
        unreadCount={unreadTotal}
        assignedUserUuid={assignedUserUuid}
        onAssignedUserChange={setAssignedUserUuid}
      />

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading && conversations.length === 0 ? (
          <div className="text-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-brandColor mx-auto" />
          </div>
        ) : error ? (
          <div className="text-center py-16 px-4 text-sm text-red-600">{error}</div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-16 px-4">
            <MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No conversations match these filters.</p>
          </div>
        ) : (
          conversations.map((c) => (
            <ConversationListItem
              key={c.uuid}
              conversation={c}
              active={c.uuid === activeUuid}
              onClick={() => handleSelect(c)}
            />
          ))
        )}

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-500">
            <button
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
            >
              Prev
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => goToPage(page + 1)}
              className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showStartModal && (
        <StartConversationModal
          onClose={() => setShowStartModal(false)}
          onResolved={(conv) => { refresh(); onSelect(conv); }}
        />
      )}
    </div>
  );
};

export default ConversationList;
